import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { IconComponent } from '@mode/capra';
import {
  ScheduleModalFacade,
  ScheduleOwnerFacade,
  ScheduleResource,
  ScheduleSlackFacade,
  ScheduleSubscriptionFacade,
  SchedulesFacade,
} from '@mode/shared/schedules/contract';
import { CadenceConfigComponent, DeliveryComponent, ParametersComponent } from '@mode/shared/schedules/ui';
import {
  EmailSettingsComponent,
  HeaderComponent,
  LoaderComponent,
  SlackChannelSelectorComponent,
  StatusBannerComponent,
} from '@mode/shared/ui';
import { HelpDocsService } from '@mode/shared/util-js';
import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { DataViewFacade } from '@mode/switch-views/contract';
import { NoticesScrolling, SCROLL_SERVICE, ScrollService } from '../scroll-service';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { filter, first, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { AccountFacade } from '@mode/accounts/contract';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    CadenceConfigComponent,
    DeliveryComponent,
    HeaderComponent,
    IconComponent,
    LoaderComponent,
    EmailSettingsComponent,
    ParametersComponent,
    SlackChannelSelectorComponent,
    StatusBannerComponent,
  ],
  providers: [
    {
      provide: SCROLL_SERVICE,
      useClass: ScrollService,
    },
  ],
  selector: 'mode-schedule-editor-container',
  templateUrl: './schedule-editor-container.component.html',
  styleUrls: ['./schedule-editor-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleEditorContainerComponent implements OnInit, OnDestroy {
  public thoughtSpotIntegrated$ = this.thoughtspotIntegrationService.currOrgIntegrated$;
  public availableIntervals$!: Observable<string[]>;
  public selectedScheduleFrequency!: string;
  public isSelectedFrequencyAvailable$!: Observable<boolean>;
  public adminManagedScheduleIntervalsEnabled$: Observable<boolean>;
  public showThresholdBanner = false;
  public orgName!: string;
  orgContext$ = this._accountFacade.currentOrg$;
  public bannerMsg =
    'This schedule refreshes more frequently than is allowed by your Workspace. A supported cadence is suggested below.';

  private destroy$ = new Subject<void>();

  constructor(
    public dataViewFacade: DataViewFacade,
    public scheduleOwnerFacade: ScheduleOwnerFacade,
    public scheduleModalFacade: ScheduleModalFacade,
    public scheduleSubscriptionFacade: ScheduleSubscriptionFacade,
    public scheduleSlackFacade: ScheduleSlackFacade,
    @Inject(SCROLL_SERVICE) public scrollService: NoticesScrolling,
    public helpDocs: HelpDocsService,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService,
    private schedulesFacade: SchedulesFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private _accountFacade: AccountFacade
  ) {
    this.adminManagedScheduleIntervalsEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.AdminManagedScheduleIntervals
    );
  }

  @Input() scheduleEditContainerInputs = {
    schedule: {} as unknown as ScheduleResource,
    isNewSchedule: false,
    email: 'test@test.com',
    isDataset: false,
    showScheduleEditForm: false,
    notifyType: 'none',
    param: {
      defaultOptions: [],
      options: [],
      selectedParams: {},
    },
    paramsExist: false,
    reportParameterOptionsLoading: false,
    reportParameterOptionsComplete: false,
    slackDeliveryOptionEnabled: false,
    subscription: {
      data_previews_enabled: false,
      data_tables_enabled: false,
      csv_attachments_enabled: false,
      pdf_attachments_enabled: false,
      report_links_enabled: false,
    },
  };

  @Output() dayOfMonthChanged = new EventEmitter<number>();
  @Output() dayOfWeekChanged = new EventEmitter<number>();
  @Output() startTimeChanged = new EventEmitter<number>();
  @Output() timeChanged = new EventEmitter<any>();
  @Output() deliveryTypeChanged = new EventEmitter<any>();
  @Output() testEmail = new EventEmitter<void>();
  @Output() updateParam = new EventEmitter<any>();
  @Output() updateMultiSelectAngular = new EventEmitter<any>();

  onScroll(event: { target: Element }) {
    const bottomSection = event.target.querySelector('[data-id="bottom-section"]');

    if (bottomSection !== null) {
      this.scrollService.upperElementScrolled(bottomSection);
    }
  }

  ngOnInit() {
    this.selectedScheduleFrequency = this.scheduleModalFacade.activeSchedule.cleanSchedule.frequency;

    this.orgContext$
      .pipe(
        filter((org) => !!org?.username),
        first(),
        map((org) => {
          this.orgName = org.username;
          this.schedulesFacade.loadScheduleIntervalThreshold(this.orgName);
        })
      )
      .subscribe();
    this.availableIntervals$ = this.schedulesFacade.availableIntervals$;

    this.isSelectedFrequencyAvailable$ = this.adminManagedScheduleIntervalsEnabled$.pipe(
      switchMap((isEnabled) => {
        if (!isEnabled) return of(true);
        return this.availableIntervals$.pipe(map((intervals) => intervals.includes(this.selectedScheduleFrequency)));
      })
    );

    this.isSelectedFrequencyAvailable$.pipe(takeUntil(this.destroy$)).subscribe((isAvailable) => {
      this.showThresholdBanner = !this.scheduleEditContainerInputs.isNewSchedule && !isAvailable;
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
